import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";


export default function VehiclesPage() {
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCity, setFilterCity] = useState("");
    const [reserving, setReserving] = useState(null); // vehicle being reserved

    const load = async () => {
        try {
            const params = filterCity ? { location: filterCity } : {};
            const { data } = await api.get("/vehicles", { params });
            setVehicles(data);
        } catch {
            // public, no fail
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCity]);

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-6xl mx-auto w-full px-6 sm:px-10 py-16">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
                    <div>
                        <div className="overline mb-3">SMART Wheels</div>
                        <h1 className="font-serif text-4xl sm:text-5xl">Rent a vehicle</h1>
                        <p className="text-sm text-muted-foreground mt-3 max-w-xl">
                            Verified Jamaican vehicle owners. Daily rates, instant requests, owner pickup or door delivery.
                        </p>
                    </div>
                    <Button
                        onClick={() => navigate("/vehicles/list")}
                        className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11 px-6"
                        data-testid="list-vehicle-cta"
                    >
                        <Plus size={14} className="mr-2" /> List your vehicle
                    </Button>
                </div>

                <div className="mb-8 flex flex-wrap gap-2">
                    {["", "Kingston", "Montego Bay", "Ocho Rios", "Negril", "Portmore"].map((c) => (
                        <button
                            key={c || "all"}
                            onClick={() => setFilterCity(c)}
                            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] border rounded-sm ${
                                filterCity === c ? "border-primary gold-text" : "border-white/10 text-muted-foreground hover:border-white/30"
                            }`}
                            data-testid={`vehicle-filter-${c || "all"}`}
                        >
                            {c || "All Jamaica"}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-muted-foreground">Loading vehicles…</p>
                ) : vehicles.length === 0 ? (
                    <div className="text-center py-20 border border-dashed border-white/10 rounded-sm">
                        <Car size={32} className="gold-text mx-auto mb-4" strokeWidth={1.2} />
                        <p className="text-muted-foreground">No vehicles available yet in this area.</p>
                        <p className="text-xs text-muted-foreground mt-2">Be the first owner — list yours and earn from idle days.</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {vehicles.map((v) => (
                            <div
                                key={v.id}
                                className="border border-white/10 bg-card rounded-sm overflow-hidden hover:border-primary/40 transition-colors"
                                data-testid={`vehicle-card-${v.id}`}
                            >
                                <div className="aspect-[4/3] bg-muted overflow-hidden">
                                    {v.image_url ? (
                                        <img src={v.image_url} alt={`${v.make} ${v.model}`}
                                             className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                            <Car size={48} strokeWidth={1.2} />
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className="font-serif text-xl">{v.make} {v.model}</div>
                                        <span className="text-[10px] uppercase tracking-[0.2em] gold-text whitespace-nowrap">
                                            ${v.daily_rate.toLocaleString()}/day
                                        </span>
                                    </div>
                                    <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
                                        <span>{v.year}</span>
                                        <span>· {v.transmission}</span>
                                        <span>· {v.seats} seats</span>
                                    </div>
                                    <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1">
                                        <MapPin size={12} strokeWidth={1.5} /> {v.location}
                                    </div>
                                    <Button
                                        onClick={() => setReserving(v)}
                                        className="mt-4 w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-10"
                                        data-testid={`reserve-${v.id}`}
                                    >
                                        Reserve
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {reserving && (
                    <ReserveModal
                        vehicle={reserving}
                        onClose={() => setReserving(null)}
                        onSuccess={() => {
                            setReserving(null);
                            navigate("/my-bookings?tab=vehicles");
                        }}
                    />
                )}
            </main>
            <Footer />
        </div>
    );
}


function ReserveModal({ vehicle, onClose, onSuccess }) {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [pickup, setPickup] = useState(vehicle.location);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const days = (() => {
        if (!start || !end) return 0;
        const s = new Date(start), e = new Date(end);
        if (e < s) return 0;
        return Math.round((e - s) / 86400000) + 1;
    })();
    const subtotal = days * vehicle.daily_rate;

    const submit = async () => {
        if (!start || !end) return toast.error("Pick both dates");
        if (days <= 0) return toast.error("End date must be on or after start date");
        setSubmitting(true);
        try {
            await api.post(`/vehicles/${vehicle.id}/bookings`, {
                vehicle_id: vehicle.id,
                start_date: start,
                end_date: end,
                pickup_location: pickup,
                notes,
            });
            toast.success(`Request sent to ${vehicle.owner_name || "owner"}. They'll confirm shortly.`);
            onSuccess();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
            data-testid="reserve-modal"
        >
            <div
                className="bg-card border border-white/10 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-white/10">
                    <div className="overline mb-1">Reserve</div>
                    <div className="font-serif text-2xl">{vehicle.make} {vehicle.model}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        ${vehicle.daily_rate.toLocaleString()}/day · {vehicle.location}
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="From">
                            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                                   className="bg-input border-white/10 h-11 rounded-sm" data-testid="reserve-start" />
                        </Field>
                        <Field label="To">
                            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                                   className="bg-input border-white/10 h-11 rounded-sm" data-testid="reserve-end" />
                        </Field>
                    </div>
                    <Field label="Pickup location">
                        <Input value={pickup} onChange={(e) => setPickup(e.target.value)}
                               className="bg-input border-white/10 h-11 rounded-sm" data-testid="reserve-pickup" />
                    </Field>
                    <Field label="Notes (optional)">
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Driver licence age, special requests…"
                                  className="bg-input border-white/10 rounded-sm" data-testid="reserve-notes" />
                    </Field>

                    {days > 0 && (
                        <div className="p-4 bg-muted/30 border border-white/5 rounded-sm" data-testid="reserve-summary">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{days} {days === 1 ? "day" : "days"} × ${vehicle.daily_rate.toLocaleString()}</span>
                                <span className="font-serif text-lg gold-text">${subtotal.toLocaleString()}</span>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                                Owner has 24 hours to accept. Payment is taken once they confirm.
                            </p>
                        </div>
                    )}
                </div>
                <div className="p-6 pt-0 flex gap-2">
                    <Button onClick={onClose} variant="outline"
                            className="border-white/10 rounded-sm uppercase tracking-[0.2em] text-xs h-11 flex-1">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={submitting || days <= 0}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11 flex-1"
                            data-testid="reserve-submit">
                        {submitting ? "Sending…" : "Request"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


export function ListVehiclePage() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        make: "", model: "", year: 2020, plate: "",
        color: "", vehicle_type: "sedan", transmission: "automatic",
        seats: 4, daily_rate: "", location: "Kingston", description: "",
    });
    const [mine, setMine] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get("/vehicles/me");
            setMine(data);
        } catch {/**/}
    };
    useEffect(() => { refresh(); }, []);

    const submit = async () => {
        if (!form.make || !form.model || !form.plate || !form.daily_rate) {
            toast.error("Make, model, plate, and daily rate are required");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vehicles", {
                ...form,
                year: Number(form.year),
                seats: Number(form.seats),
                daily_rate: Number(form.daily_rate),
            });
            toast.success("Vehicle listed — pending admin review.");
            setForm({ ...form, plate: "", description: "" });
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-3xl mx-auto w-full px-6 sm:px-10 py-16">
                <div className="overline mb-3">SMART Wheels</div>
                <h1 className="font-serif text-4xl sm:text-5xl mb-2">List your vehicle</h1>
                <p className="text-sm text-muted-foreground mb-10">
                    Earn JMD from your idle days. Admin reviews each listing within 24 hours; approved vehicles go live to renters island-wide.
                </p>

                <div className="space-y-5 p-6 border border-white/10 rounded-sm bg-card mb-10">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Make"><Input value={form.make} onChange={(e) => update("make", e.target.value)} placeholder="Toyota" data-testid="veh-make" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Model"><Input value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="Probox" data-testid="veh-model" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Year"><Input type="number" value={form.year} onChange={(e) => update("year", e.target.value)} data-testid="veh-year" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Plate (Jamaica)"><Input value={form.plate} onChange={(e) => update("plate", e.target.value.toUpperCase())} placeholder="ABC-123" data-testid="veh-plate" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Color"><Input value={form.color} onChange={(e) => update("color", e.target.value)} placeholder="Pearl White" data-testid="veh-color" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Type">
                            <select value={form.vehicle_type} onChange={(e) => update("vehicle_type", e.target.value)} className="w-full bg-input border border-white/10 h-11 rounded-sm px-3 text-sm" data-testid="veh-type">
                                <option value="sedan">Sedan</option>
                                <option value="suv">SUV</option>
                                <option value="hatchback">Hatchback</option>
                                <option value="minivan">Minivan</option>
                                <option value="luxury">Luxury</option>
                            </select>
                        </Field>
                        <Field label="Transmission">
                            <select value={form.transmission} onChange={(e) => update("transmission", e.target.value)} className="w-full bg-input border border-white/10 h-11 rounded-sm px-3 text-sm" data-testid="veh-trans">
                                <option value="automatic">Automatic</option>
                                <option value="manual">Manual</option>
                            </select>
                        </Field>
                        <Field label="Seats"><Input type="number" value={form.seats} onChange={(e) => update("seats", e.target.value)} data-testid="veh-seats" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Daily rate (JMD)"><Input type="number" value={form.daily_rate} onChange={(e) => update("daily_rate", e.target.value)} placeholder="7500" data-testid="veh-rate" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                        <Field label="Location"><Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Kingston" data-testid="veh-location" className="bg-input border-white/10 h-11 rounded-sm" /></Field>
                    </div>
                    <Field label="Description (optional)">
                        <Textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)}
                                  placeholder="Air-conditioned, full tank pickup, smoke-free…"
                                  className="bg-input border-white/10 rounded-sm" data-testid="veh-desc" />
                    </Field>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                        data-testid="veh-submit"
                    >
                        {submitting ? "Submitting…" : "Submit listing for review"}
                    </Button>
                </div>

                <div className="overline mb-4">Your listings</div>
                {mine.length === 0 ? (
                    <p className="text-sm text-muted-foreground">You haven't listed a vehicle yet.</p>
                ) : (
                    <div className="space-y-3">
                        {mine.map((v) => (
                            <div key={v.id} className="p-4 border border-white/10 rounded-sm bg-card flex justify-between items-center" data-testid={`my-veh-${v.id}`}>
                                <div>
                                    <div className="font-serif text-lg">{v.make} {v.model} <span className="text-xs text-muted-foreground ml-2">{v.plate}</span></div>
                                    <div className="text-[11px] text-muted-foreground mt-1 flex gap-3 items-center">
                                        <span className={`px-2 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.2em] ${
                                            v.status === "approved" ? "border-emerald-700/40 text-emerald-300"
                                            : v.status === "suspended" ? "border-red-700/40 text-red-300"
                                            : "border-amber-700/40 text-amber-300"
                                        }`}>{v.status}</span>
                                        <span>${v.daily_rate.toLocaleString()}/day · {v.location}</span>
                                    </div>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    {v.available ? "Live" : "Paused"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}


function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-2 block text-[10px]">{label}</Label>
            {children}
        </div>
    );
}
